// FILE: InspectionResult.jsx (REPLACE with this complete code)
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import SignInHeader from "../auth/SigninHeader";
import Navigation from "../layout/Navigation";

const InspectionResult = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [authenticated, setAuthenticated] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedTemplate, setSelectedTemplate] = useState("");
    const [group, setGroup] = useState("");
    const [type, setType] = useState("");
    const [subtype, setSubType] = useState("");
    const [category, setCategory] = useState("");
    const [selectedG6Type, setSelectedG6Type] = useState("");
    const [inspectionResults, setInspectionResults] = useState([]);
    const [loadingResults, setLoadingResults] = useState(false);
    const [user, setUser] = useState(null);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [backendError, setBackendError] = useState("");
    const [selectedWorkflowStatus, setSelectedWorkflowStatus] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false); // State for Next button loading

    useEffect(() => {
        const token = localStorage.getItem("token");
        const storedUser = localStorage.getItem("user");
        const savedTheme = localStorage.getItem("theme");
        const prefersDark =
            window.matchMedia &&
            window.matchMedia("(prefers-color-scheme: dark)").matches;
        
        setIsDarkMode(savedTheme === "dark" || (!savedTheme && prefersDark));
        
        if (storedUser) {
            try {
                setUser(JSON.parse(storedUser));
            } catch (e) {
                console.error('Error parsing stored user:', e);
                localStorage.removeItem('user');
            }
        }

        const { selectedTemplate, group, type, subtype, category, selectedG6Type } = location.state || {};
        setSelectedTemplate(selectedTemplate || localStorage.getItem("selectedTemplate") || "AA - Received");
        setGroup(group || localStorage.getItem("group") || "Building");
        setType(type || localStorage.getItem("type") || "Residential");
        setSubType(subtype || localStorage.getItem("subtype") || "NA");
        setCategory(category || localStorage.getItem("category") || "NA");
        setSelectedG6Type(selectedG6Type || localStorage.getItem("selectedG6Type") || "");

        const isAuthenticated = !!token;
        setAuthenticated(isAuthenticated);

        if (!isAuthenticated) {
            setErrorMessage("Please sign in to access this page.");
            navigate("/signin");
            return;
        }
    }, [navigate, location.state]);

    useEffect(() => {
        if (authenticated && selectedG6Type) {
            const sendInspectionTypeToBackend = async () => {
                try {
                    setLoadingResults(true);
                    setBackendError("");
                    
                    const token = localStorage.getItem("token");
                    if (!token) {
                        setBackendError("Authentication token missing. Please sign in.");
                        navigate("/signin");
                        return;
                    }

                    const response = await axios.post(
                        "http://localhost:5000/api/save-inspection-type",
                        { inspectionType: selectedG6Type },
                        {
                            headers: { Authorization: token },
                        }
                    );

                    console.log('Save inspection type response:', response.data);

                    if (response.data.success) {
                        const resultString = response.data.InspectionTypeResult || "";
                        if (resultString && resultString.includes("No Inspection Results found")) {
                            setInspectionResults([]);
                        } else {
                            const results = resultString
                                .split("\n")
                                .filter(line => line.startsWith("- "))
                                .map(line => line.replace("- ", "").trim());
                            setInspectionResults(results);
                        }
                    } else {
                        setBackendError(response.data.message || "Failed to save inspection type.");
                        setInspectionResults([]);
                    }
                } catch (error) {
                    console.error("Error sending inspection type to backend:", error);
                    
                    if (error.response?.status === 401) {
                        setBackendError("Your session has expired. Please sign in again.");
                        localStorage.removeItem('token');
                        navigate("/signin");
                    } else {
                        setBackendError("Failed to save inspection type. Please check console for details.");
                        setInspectionResults([]);
                    }
                } finally {
                    setLoadingResults(false);
                }
            };
            sendInspectionTypeToBackend();
        }
    }, [authenticated, selectedG6Type, navigate]);

    const toggleDarkMode = () => {
        const newMode = !isDarkMode;
        setIsDarkMode(newMode);
        localStorage.setItem("theme", newMode ? "dark" : "light");
    };

    const handleTemplateClick = () => {
        if (selectedTemplate) {
            navigate("/template-viewer", { state: { selectedTemplate } });
        }
    };

    const handleRecordClick = () => {
        if (group && type && subtype && category) {
            navigate("/record-types", {
                state: { group, type, subtype, category },
            });
        }
    };

    const handleInspectionTypeClick = () => {
        if (selectedG6Type) {
            navigate("/inspection-type", {
                state: { selectedTemplate, group, type, subtype, category, selectedG6Type },
            });
        }
    };

    const handleNextClick = async () => {
        if (!selectedWorkflowStatus) {
            setBackendError("Please select an inspection result before proceeding.");
            return;
        }
        
        setIsSubmitting(true);
        setBackendError("");

        try {
            const token = localStorage.getItem("token");
            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/save-inspection-result`,
                { inspectionResult: selectedWorkflowStatus },
                { headers: { Authorization: token } }
            );

            localStorage.setItem("selectedInspectionResult", selectedWorkflowStatus);

            navigate("/contact-types", {
                state: {
                    selectedTemplate,
                    group,
                    type,
                    subtype,
                    category,
                    selectedG6Type,
                    selectedWorkflowStatus
                }
            });
        } catch (error) {
            console.error("Error saving inspection result:", error);
            const message = error.response?.data?.message || "Failed to save your selection. Please try again.";
            setBackendError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!authenticated) {
        return (
            <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-main-dark' : 'bg-gray-100'}`}>
                <div className="text-center p-6 rounded-xl bg-red-50 text-red-600 max-w-md">
                    <div className="mb-4">
                        <span className="text-2xl">❌</span>
                    </div>
                    <p className="text-lg font-medium">{errorMessage}</p>
                    <button
                        onClick={() => navigate('/signin')}
                        className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                        Go to Sign In
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={`flex flex-col min-h-screen transition-colors duration-300 ${isDarkMode ? "bg-main-dark text-white" : "bg-gray-100 text-gray-900"}`}>
            <SignInHeader
                user={user}
                isLoggedIn={authenticated}
                isDarkMode={isDarkMode}
                toggleDarkMode={toggleDarkMode}
            />
            <Navigation
                activeTab="My Apps"
                setActiveTab={() => {}}
                isDarkMode={isDarkMode}
                toggleDarkMode={toggleDarkMode}
            />

            <div className="flex flex-col items-center pt-[140px] pb-10 px-5 flex-grow">
                <div className="self-start ml-6 mt-4 mb-3 px-3 py-2">
                    <button onClick={handleTemplateClick} className="bg-transparent border-none text-purple-700 font-medium cursor-pointer text-sm hover:text-purple-800" disabled={!selectedTemplate}>
                        {selectedTemplate || "No Template Selected"} |
                    </button>
                    <button onClick={handleRecordClick} className="ml-1 bg-transparent border-none text-purple-700 font-medium cursor-pointer text-sm hover:text-purple-800" disabled={!group || !type || !subtype || !category}>
                        {`${group}/${type}/${subtype}/${category}`} |
                    </button>
                    <button onClick={handleInspectionTypeClick} className="ml-1 bg-transparent border-none text-purple-700 font-medium cursor-pointer text-sm hover:text-purple-800" disabled={!selectedG6Type}>
                        {selectedG6Type || "No Inspection Type Selected"}
                    </button>
                </div>

                {backendError && (
                    <div className="w-full max-w-md mb-4 p-3 rounded bg-red-50 border border-red-200">
                        <div className="flex items-center justify-between">
                            <p className="text-red-600 text-sm">{backendError}</p>
                            <button onClick={() => setBackendError("")} className="text-red-400 hover:text-red-600 text-lg">
                                ×
                            </button>
                        </div>
                    </div>
                )}

                <div className="w-full max-w-md">
                    <div className="mb-6">
                        <div className="relative">
                            <div className="bg-[#4a4a68] lg shadow-md">
                                <div className="px-4 py-3 bg-[#4a4a68] text-white text-sm font-medium rounded-t-lg">
                                    Select inspection result...
                                </div>
                                <div className="bg-[#4a4a68] px-4 py-3 rounded-b-lg">
                                    {loadingResults ? (
                                        <div className="text-center text-gray-300 py-2">Loading inspection results...</div>
                                    ) : inspectionResults.length > 0 ? (
                                        <select
                                            className="w-full bg-[#e8e8f0] text-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            value={selectedWorkflowStatus}
                                            onChange={(e) => {
                                                setSelectedWorkflowStatus(e.target.value);
                                                setBackendError("");
                                            }}
                                        >
                                            <option value="" disabled>Select an inspection result</option>
                                            {inspectionResults.map((result, index) => (
                                                <option key={index} value={result}>
                                                    {result}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <div className="text-center text-gray-300 py-2">
                                            No inspection results found for the selected inspection type.
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="absolute right-0 top-0 bg-[#2a2a48] text-white px-4 py-4 rounded-r-lg">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-center">
                        <button
                            onClick={handleNextClick}
                            className={`font-medium px-8 py-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                selectedWorkflowStatus && !loadingResults && !isSubmitting
                                    ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                                    : 'bg-gray-400 text-gray-600 cursor-not-allowed'
                            }`}
                            disabled={!selectedWorkflowStatus || loadingResults || isSubmitting}
                        >
                            {isSubmitting ? 'Saving...' : (loadingResults ? 'Loading...' : 'Next')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InspectionResult;